// Importing utilities for class name merging
import { type ClassValue, clsx } from 'clsx'; // `clsx` helps conditionally join class names
import { twMerge } from 'tailwind-merge'; // `twMerge` resolves class name conflicts in Tailwind CSS

/**
 * Combines and merges class names using clsx and tailwind-merge.
 * @param inputs - Class names or expressions to conditionally include.
 * @returns A single string of merged class names.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Async Utility: Creates a delay for a specified number of milliseconds.
 * @param ms - The time to delay in milliseconds.
 * @returns A promise that resolves after the delay.
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Async Utility: Creates a debounced function that delays invoking the provided function.
 * @param fn - The function to debounce.
 * @param wait - The debounce delay in milliseconds.
 * @returns A debounced version of the function.
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return function (...args: Parameters<T>) {
    clearTimeout(timeout); // Clear the existing timeout
    timeout = setTimeout(() => fn(...args), wait); // Set a new timeout
  };
}

/**
 * Async Utility: Retries a promise-based function with exponential backoff.
 * @param fn - The function to retry.
 * @param maxAttempts - Maximum number of attempts.
 * @param baseDelay - Initial delay in milliseconds.
 * @returns The resolved value of the function, or throws the last error.
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn(); // Try executing the function
    } catch (error) {
      lastError = error as Error;
      if (attempt === maxAttempts) break; // Stop after maxAttempts
      await delay(baseDelay * Math.pow(2, attempt - 1)); // Exponential backoff
    }
  }

  throw lastError; // Throw the last encountered error
}

/**
 * Formats a date string into a human-readable format in French locale.
 * @param dateString - The date string to format.
 * @returns The formatted date string.
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Checks if a given date is in the past.
 * @param date - A Date object or date string.
 * @returns True if the date is in the past, otherwise false.
 */
export function isPastDate(date: Date | string): boolean {
  const compareDate = date instanceof Date ? date : new Date(date);
  return compareDate < new Date();
}

/**
 * Returns a human-readable relative time string (e.g., "in 3 days", "5 hours ago").
 * @param date - A Date object or date string.
 * @returns The relative time string.
 */
export function getRelativeTimeString(date: Date | string): string {
  const compareDate = date instanceof Date ? date : new Date(date);
  const formatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
  const diff = compareDate.getTime() - Date.now();
  const diffDays = Math.round(diff / (1000 * 60 * 60 * 24));
  
  if (Math.abs(diffDays) < 1) {
    const diffHours = Math.round(diff / (1000 * 60 * 60));
    return formatter.format(diffHours, 'hour');
  }
  
  return formatter.format(diffDays, 'day');
}

/**
 * Formats a number into a currency string in euros.
 * @param amount - The amount to format.
 * @returns The formatted currency string.
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Formats a salary range into a readable string.
 * @param min - The minimum salary.
 * @param max - The maximum salary (optional).
 * @returns The formatted salary range string.
 */
export function formatSalaryRange(min: number, max?: number): string {
  const formattedMin = formatCurrency(min);
  if (!max || min === max) return formattedMin; // Single value if min equals max
  const formattedMax = formatCurrency(max);
  return `${formattedMin} - ${formattedMax}`;
}

/**
 * Generates a random alphanumeric string of the specified length.
 * @param length - The length of the string to generate.
 * @returns A random alphanumeric string.
 */
export function randomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

/**
 * Truncates a string to a specified maximum length, adding a suffix if truncated.
 * @param str - The string to truncate.
 * @param maxLength - The maximum allowed length.
 * @param suffix - The suffix to append (default is "...").
 * @returns The truncated string.
 */
export function truncate(str: string, maxLength: number, suffix = '...'): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Converts a string to title case.
 * @param str - The string to convert.
 * @returns The title-cased string.
 */
export function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );
}

/**
 * Validates if a value is defined (not null or undefined).
 * @param value - The value to check.
 * @returns True if the value is defined, otherwise false.
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Validates if an email address is in a valid format.
 * @param email - The email address to validate.
 * @returns True if the email is valid, otherwise false.
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates if a phone number is in a valid French format.
 * @param phone - The phone number to validate.
 * @returns True if the phone number is valid, otherwise false.
 */
export function isValidPhoneNumber(phone: string): boolean {
  const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
  return phoneRegex.test(phone);
}

/**
 * Formats a salary number into a readable string with the EUR currency symbol.
 * @param salary - The salary number to format.
 * @returns The formatted salary string.
 */
export function formatSalary(salary: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(salary);
}

/**
 * Formats a decimal number into a percentage string.
 * @param value - The decimal value (e.g., 0.25 for 25%).
 * @param decimals - The number of decimal places (default is 0).
 * @returns The formatted percentage string.
 */
export function formatPercent(value: number, decimals = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

// WhatsApp-specific constants
export const WHATSAPP_NUMBER = '+33745800515';
export const WHATSAPP_MESSAGE = 'Bonjour je souhaite déposer mon CV';

/**
 * Generates a WhatsApp link with an optional custom message.
 * @param message - Optional custom message (defaults to WHATSAPP_MESSAGE).
 * @returns WhatsApp API URL.
 */
export function getWhatsAppLink(message = WHATSAPP_MESSAGE): string {
  return `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(message)}`;
}
